import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { templatesApi, bullhornApi, massMergeApi, getErrorMessage, resolveBackendUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Search, X, PenLine, FileStack, Download, Send } from 'lucide-react';
import { useSenderFillableFields } from '../hooks/useSenderFillableFields';
import SignatureCaptureDialog from '../components/send/SignatureCaptureDialog';

// "Mass Merge Candidates" — one placement template, mapped to Bullhorn once
// (exactly like any other placement template), run against MANY placements
// at once. Output is a single combined PDF, one page-block per placement —
// either downloaded directly, or sent to a Bullhorn contact via the
// template's own "send to client contact" magic-link setting. Never written
// back to Bullhorn. See routes/massMerge.ts.
export default function MassMergePage() {
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateId, setTemplateId] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPlacements, setSelectedPlacements] = useState([]);

  const [companyQuery, setCompanyQuery] = useState('');
  const [companySearching, setCompanySearching] = useState(false);
  const [companyResults, setCompanyResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompanyPlacements, setLoadingCompanyPlacements] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { merge_url, placements_merged, placements_failed }

  const [contactQuery, setContactQuery] = useState('');
  const [contactSearching, setContactSearching] = useState(false);
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { document_id, sent_to, placements_merged, placements_failed }

  const selectedTemplate = templates.find((t) => t.id === templateId) || null;
  // Reuses the template's own "Placement routing" setting (Send to client
  // contact + Interactive link, in the Template Editor's Document Settings)
  // — the same gate a normal contact send already enforces server-side, not
  // a rule invented for mass merge.
  const canSendToContact = !!selectedTemplate?.send_to_contact && selectedTemplate?.contact_delivery_mode === 'interactive';

  // Reuse the same signature-capture machinery used for a sender's own
  // Requester/Admin field elsewhere in the app — here it's marked
  // sender_fillable purely for this hook's own local bookkeeping (never
  // sent to the backend), since a mass-merge run has no live per-placement
  // signing session: one signature is captured once and stamped identically
  // onto every generated page.
  const activeTemplatesForSignature = selectedTemplate
    ? [{
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        fields: (selectedTemplate.fields || []).map((f) =>
          f.field_type === 'signature' ? { ...f, sender_fillable: true } : f),
      }]
    : [];
  const {
    senderFieldKey,
    signingKey, setSigningKey,
    signatureMode, setSignatureMode,
    typedSignature, setTypedSignature,
    signaturePadRef, initSignatureCanvas, saveSignature,
    senderFillableFields,
    buildPrefilledValues,
  } = useSenderFillableFields(activeTemplatesForSignature);
  const signatureField = senderFillableFields[0]?.field || null;
  const signatureValue = signatureField
    ? buildPrefilledValues([selectedTemplate.id])[signatureField.id]
    : null;

  useEffect(() => {
    (async () => {
      try {
        const response = await templatesApi.list();
        setTemplates((response.data || []).filter((t) => t.entity_type === 'placement'));
      } catch (error) {
        toast.error('Failed to load templates');
      } finally {
        setLoadingTemplates(false);
      }
    })();
  }, []);

  // Changing templates invalidates any queued placements/results/signature
  // from a previous mapping — nothing here carries over between templates.
  useEffect(() => {
    setSelectedPlacements([]);
    setResult(null);
    setSelectedContact(null);
    setContactResults([]);
    setSendResult(null);
    setSelectedCompany(null);
    setCompanyResults([]);
    setSearchResults([]);
  }, [templateId]);

  const search = async () => {
    if (searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const response = await bullhornApi.searchPlacements(searchQuery);
      setSearchResults(response.data.placements || []);
      if (!(response.data.placements || []).length) {
        toast.info(`No placements matched "${searchQuery}".`);
      }
    } catch (error) {
      toast.error('Failed to search placements');
    } finally {
      setSearching(false);
    }
  };

  const searchCompanies = async () => {
    if (companyQuery.trim().length < 2) return;
    setCompanySearching(true);
    try {
      const response = await bullhornApi.searchCompanies(companyQuery);
      setCompanyResults(response.data.companies || []);
      if (!(response.data.companies || []).length) {
        toast.info(`No companies matched "${companyQuery}".`);
      }
    } catch (error) {
      toast.error('Failed to search companies');
    } finally {
      setCompanySearching(false);
    }
  };

  const selectCompany = async (company) => {
    setSelectedCompany(company);
    setCompanyResults([]);
    setLoadingCompanyPlacements(true);
    try {
      const response = await bullhornApi.searchPlacementsByCompany(company.id);
      setSearchResults(response.data.placements || []);
      if (!(response.data.placements || []).length) {
        toast.info(`No placements found for ${company.name}.`);
      }
    } catch (error) {
      toast.error('Failed to load placements for that company');
    } finally {
      setLoadingCompanyPlacements(false);
    }
  };

  const clearCompanyFilter = () => {
    setSelectedCompany(null);
    setSearchResults([]);
  };

  const addPlacement = (p) => {
    if (selectedPlacements.some((sp) => sp.id === p.id)) return;
    setSelectedPlacements((prev) => [...prev, {
      id: p.id,
      name: p.candidate?.name || `Placement #${p.id}`,
      jobTitle: p.jobOrder?.title || '',
      companyName: p.company?.name || '',
    }]);
  };

  const removePlacement = (id) => {
    setSelectedPlacements((prev) => prev.filter((p) => p.id !== id));
  };

  const generate = async () => {
    if (!templateId || !selectedPlacements.length) return;
    setGenerating(true);
    setResult(null);
    try {
      const response = await massMergeApi.generate({
        template_id: templateId,
        placement_ids: selectedPlacements.map((p) => p.id),
        signature_data_url: signatureValue || null,
      });
      setResult(response.data);
      if (response.data.placements_failed?.length) {
        toast.warning(`${response.data.placements_failed.length} placement(s) could not be resolved — merged the rest.`);
      } else {
        toast.success('Merged PDF generated');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate the merged PDF'));
    } finally {
      setGenerating(false);
    }
  };

  const searchContacts = async () => {
    if (contactQuery.trim().length < 2) return;
    setContactSearching(true);
    try {
      const response = await bullhornApi.searchContacts(contactQuery);
      const results = (response.data.contacts || []).map((c) => ({
        ...c,
        name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      }));
      setContactResults(results);
      if (!results.length) toast.info(`No contacts matched "${contactQuery}".`);
    } catch (error) {
      toast.error('Failed to search contacts');
    } finally {
      setContactSearching(false);
    }
  };

  const sendToContact = async () => {
    if (!templateId || !selectedPlacements.length || !selectedContact) return;
    setSending(true);
    setSendResult(null);
    try {
      const response = await massMergeApi.sendToContact({
        template_id: templateId,
        placement_ids: selectedPlacements.map((p) => p.id),
        contact: {
          bullhorn_id: selectedContact.id,
          name: selectedContact.name,
          email: selectedContact.email,
        },
      });
      setSendResult(response.data);
      if (response.data.placements_failed?.length) {
        toast.warning(`Sent, but ${response.data.placements_failed.length} placement(s) could not be resolved.`);
      } else {
        toast.success(`Sent to ${response.data.sent_to}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send the document'));
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="mass-merge-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(getTenantPath('/send'))}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Mass Merge Candidates</h1>
            <p className="text-muted-foreground font-body mt-1">
              Map one template once, then merge it across many placements into a single downloadable PDF.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">1. Template</CardTitle>
            <CardDescription>Only placement-entity templates support mass merge.</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={templateId} onValueChange={setTemplateId} disabled={loadingTemplates}>
              <SelectTrigger data-testid="mass-merge-template-select">
                <SelectValue placeholder={loadingTemplates ? 'Loading templates…' : 'Select a placement template'} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loadingTemplates && templates.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                No placement templates found — build one in Templates first.
              </p>
            )}
          </CardContent>
        </Card>

        {templateId && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">2. Placements</CardTitle>
              <CardDescription>Search and add every placement you want a page merged for.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm">Filter by company</Label>
                {selectedCompany ? (
                  <div className="mt-1 inline-flex items-center gap-2 text-xs bg-white border rounded-lg pl-3 pr-1 py-1.5" data-testid="mass-merge-selected-company">
                    <span className="font-medium text-slate-800">{selectedCompany.name}</span>
                    <span className="text-muted-foreground">
                      {loadingCompanyPlacements ? 'loading placements…' : `${searchResults.length} placement${searchResults.length === 1 ? '' : 's'}`}
                    </span>
                    <button
                      type="button"
                      onClick={clearCompanyFilter}
                      className="ml-1 w-5 h-5 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600"
                      aria-label={`Clear ${selectedCompany.name} filter`}
                      data-testid="mass-merge-clear-company"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={companyQuery}
                      onChange={(e) => setCompanyQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchCompanies()}
                      placeholder="Search for a client company"
                      data-testid="mass-merge-company-search"
                    />
                    <Button type="button" variant="outline" onClick={searchCompanies} disabled={companySearching}>
                      <Search className="w-4 h-4 mr-1" /> Search
                    </Button>
                  </div>
                )}
                {companyResults.length > 0 && !selectedCompany && (
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto mt-2">
                    {companyResults.map((co) => (
                      <button
                        key={co.id}
                        type="button"
                        onClick={() => selectCompany(co)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center justify-between gap-2"
                        data-testid={`mass-merge-company-result-${co.id}`}
                      >
                        <span className="font-medium">{co.name}</span>
                        <span className="text-xs text-muted-foreground">Browse placements</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {!selectedCompany && (
                <div>
                  <Label className="text-sm">Or search by candidate, job title or company</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && search()}
                      placeholder="Search placements by candidate, job title or company"
                      data-testid="mass-merge-placement-search"
                    />
                    <Button type="button" variant="outline" onClick={search} disabled={searching}>
                      <Search className="w-4 h-4 mr-1" /> Search
                    </Button>
                  </div>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {searchResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addPlacement(p)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center justify-between gap-2"
                      data-testid={`mass-merge-result-${p.id}`}
                    >
                      <span>
                        <span className="font-medium">{p.candidate?.name || `Placement #${p.id}`}</span>
                        {p.jobOrder?.title && <span className="text-muted-foreground"> · {p.jobOrder.title}</span>}
                        {p.company?.name && <span className="text-muted-foreground"> · {p.company.name}</span>}
                      </span>
                      <span className="text-xs text-muted-foreground">Add</span>
                    </button>
                  ))}
                </div>
              )}

              {selectedPlacements.length > 0 && (
                <div>
                  <Label className="text-sm">
                    {selectedPlacements.length} placement{selectedPlacements.length === 1 ? '' : 's'} queued
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedPlacements.map((p) => (
                      <div
                        key={p.id}
                        className="inline-flex items-center gap-2 text-xs bg-white border rounded-lg pl-3 pr-1 py-1.5"
                        data-testid={`mass-merge-selected-${p.id}`}
                      >
                        <span className="font-medium text-slate-800">{p.name}</span>
                        {p.jobTitle && <span className="text-muted-foreground">· {p.jobTitle}</span>}
                        <button
                          type="button"
                          onClick={() => removePlacement(p.id)}
                          className="ml-1 w-5 h-5 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600"
                          aria-label={`Remove ${p.name}`}
                          data-testid={`mass-merge-remove-${p.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {signatureField && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">3. Signature (optional)</CardTitle>
              <CardDescription>
                Captured once and stamped identically onto every generated page — there's no per-candidate
                signing step in a merge like this.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSigningKey(senderFieldKey(selectedTemplate.id, signatureField.id))}
                data-testid="mass-merge-add-signature"
              >
                <PenLine className="w-4 h-4 mr-1" />
                {signatureValue ? 'Change signature' : 'Add signature'}
              </Button>
              {signatureValue && (
                <img src={signatureValue} alt="Captured signature" className="mt-3 h-16 border rounded bg-white" />
              )}
            </CardContent>
          </Card>
        )}

        {templateId && selectedPlacements.length > 0 && (
          <div className="flex items-center gap-3">
            <Button onClick={generate} disabled={generating} data-testid="mass-merge-generate">
              <FileStack className="w-4 h-4 mr-2" />
              {generating ? 'Generating…' : `Generate merged PDF (${selectedPlacements.length})`}
            </Button>
            {result && (
              <>
                <span className="text-sm text-muted-foreground">
                  {result.placements_merged} merged
                  {result.placements_failed?.length ? `, ${result.placements_failed.length} could not be resolved` : ''}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.open(resolveBackendUrl(result.merge_url), '_blank', 'noopener,noreferrer')}
                  data-testid="mass-merge-download"
                >
                  <Download className="w-4 h-4 mr-1" /> Download merged PDF
                </Button>
              </>
            )}
          </div>
        )}

        {templateId && selectedPlacements.length > 0 && !canSendToContact && (
          <p className="text-sm text-muted-foreground">
            This template isn't set up to send an interactive link to a client contact — enable
            "Send to client contact" → "Interactive link" in the template's own settings to also send this
            batch out, not just download it.
          </p>
        )}

        {templateId && selectedPlacements.length > 0 && canSendToContact && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Send to a contact</CardTitle>
              <CardDescription>
                Instead of (or as well as) downloading, email one Bullhorn contact — e.g. a site or project
                manager — a magic link to open the combined document and fill in whatever the merge couldn't
                pre-fill, once per placement, then submit.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchContacts()}
                  placeholder="Search contacts by name or email"
                  data-testid="mass-merge-contact-search"
                />
                <Button type="button" variant="outline" onClick={searchContacts} disabled={contactSearching}>
                  <Search className="w-4 h-4 mr-1" /> Search
                </Button>
              </div>

              {contactResults.length > 0 && !selectedContact && (
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {contactResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { setSelectedContact(c); setContactResults([]); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center justify-between gap-2"
                      data-testid={`mass-merge-contact-result-${c.id}`}
                    >
                      <span>
                        <span className="font-medium">{c.name || `Contact #${c.id}`}</span>
                        {c.email && <span className="text-muted-foreground"> · {c.email}</span>}
                      </span>
                      <span className="text-xs text-muted-foreground">Select</span>
                    </button>
                  ))}
                </div>
              )}

              {selectedContact && (
                <div className="inline-flex items-center gap-2 text-xs bg-white border rounded-lg pl-3 pr-1 py-1.5" data-testid="mass-merge-selected-contact">
                  <span className="font-medium text-slate-800">{selectedContact.name}</span>
                  {selectedContact.email && <span className="text-muted-foreground">· {selectedContact.email}</span>}
                  <button
                    type="button"
                    onClick={() => setSelectedContact(null)}
                    className="ml-1 w-5 h-5 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600"
                    aria-label={`Remove ${selectedContact.name}`}
                    data-testid="mass-merge-remove-contact"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  onClick={sendToContact}
                  disabled={sending || !selectedContact}
                  data-testid="mass-merge-send"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending…' : `Send to contact (${selectedPlacements.length} placements)`}
                </Button>
                {sendResult && (
                  <span className="text-sm text-muted-foreground">
                    Sent to {sendResult.sent_to} — {sendResult.placements_merged} placement
                    {sendResult.placements_merged === 1 ? '' : 's'} merged
                    {sendResult.placements_failed?.length ? `, ${sendResult.placements_failed.length} could not be resolved` : ''}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <SignatureCaptureDialog
        open={!!signingKey}
        onOpenChange={(open) => !open && setSigningKey(null)}
        mode={signatureMode}
        onModeChange={setSignatureMode}
        typedValue={typedSignature}
        onTypedValueChange={setTypedSignature}
        canvasRef={initSignatureCanvas}
        onClearCanvas={() => signaturePadRef.current?.clear()}
        onCancel={() => setSigningKey(null)}
        onSave={saveSignature}
      />
    </DashboardLayout>
  );
}
